import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';

interface PageContext {
  route: string;
  visibleSection: string;
  timeOnPage: number;
  history: string[];
}

interface TarryContextType {
  pageContext: PageContext;
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  pendingMessage: string | null;
  openWithMessage: (msg: string) => void;
  clearPendingMessage: () => void;
}

const TarryCtx = createContext<TarryContextType | null>(null);
export const useTarry = () => useContext(TarryCtx)!;

export const TarryProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [visibleSection, setVisibleSection] = useState('top');
  const [timeOnPage, setTimeOnPage] = useState(0);
  const [history, setHistory] = useState<string[]>([]);
  const [pendingMessage, setPendingMessage] = useState<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval>>();

  // Track page changes
  useEffect(() => {
    setHistory(prev => {
      const last = prev[prev.length - 1];
      if (last !== location.pathname) return [...prev, location.pathname];
      return prev;
    });
    setTimeOnPage(0);
  }, [location.pathname]);

  // Track time on page
  useEffect(() => {
    timerRef.current = setInterval(() => setTimeOnPage(t => t + 1), 1000);
    return () => clearInterval(timerRef.current);
  }, [location.pathname]);

  // Track visible sections via IntersectionObserver
  useEffect(() => {
    const sections = document.querySelectorAll('section[id], section[class*="Section"], [id*="section"]');
    if (sections.length === 0) { setVisibleSection('top'); return; }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setVisibleSection(entry.target.id || entry.target.className.split(' ')[0] || 'content');
          }
        }
      },
      { threshold: 0.3 }
    );

    sections.forEach(s => observer.observe(s));
    return () => observer.disconnect();
  }, [location.pathname]);

  const openWithMessage = useCallback((msg: string) => {
    setPendingMessage(msg);
    setIsOpen(true);
  }, []);

  const clearPendingMessage = useCallback(() => {
    setPendingMessage(null);
  }, []);

  const pageContext: PageContext = {
    route: location.pathname,
    visibleSection,
    timeOnPage,
    history,
  };

  return (
    <TarryCtx.Provider value={{ pageContext, isOpen, setIsOpen, pendingMessage, openWithMessage, clearPendingMessage }}>
      {children}
    </TarryCtx.Provider>
  );
};
